/*******************************************************************************
 * Copyright (c) 2013, 2015 EclipseSource.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ******************************************************************************/
package uk.co.nickthecoder.kojak

/*
Implementation Notes.

I removed the original's HashTable, as I didn't fully understand it, and worried that I had missed something.
e.g. it uses the java keyword `transient`, and I don't know what that means!
For large number of members ( > 32 ), I *think* the original reverts to O(n).
For fewer members, the original is faster than mine, but I don't expect it to be significantly faster???
FYI, The original's contains() method is always O(n), whereas mine uses the hash table.
*/

/**
 * Represents a JSON object, a set of name/value pairs, where the names are strings and the values
 * are JSON values.
 *
 * Members can be added using the [add] methods which accept instances of
 * [JsonValue], strings, primitive numbers, and boolean values. To modify certain values of an
 * object, use the [set] methods. Please note that the [add]
 * methods are faster than [set] as they do not search for existing members. On the other
 * hand, the [add] methods do not prevent adding multiple members with the same name.
 * Duplicate names are discouraged but not prohibited by JSON.
 *
 * Members can be accessed by their name using [get]. A list of all names can be
 * obtained from the method [names]. This class also supports iterating over the members in
 * document order using an [iterator] or an enhanced for loop:
 *
 *     for ( member in myJsonObject ) {
 *         val name = member.name
 *         val value = member.value
 *         ...
 *     }
 *
 * Even though JSON objects are unordered by definition, instances of this class preserve the order
 * of members to allow processing in document-order and to guarantee a predictable output.
 *
 * Note that this class is _not thread-safe_. If multiple threads access a
 * [JsonObject] instance concurrently, while at least one of these threads modifies the
 * contents of this object, access to the instance must be synchronized externally. Failure to do so
 * may lead to an inconsistent state.
 */
open class JsonObject() : JsonValue(), Iterable<JsonObject.Member> {

    constructor(obj: JsonObject) : this() {
        for (i in obj.names.indices) {
            add(obj.names[i], obj.values[i])
        }
    }

    private val names = mutableListOf<String>()
    private val values = mutableListOf<JsonValue>()
    private val indexHash = mutableMapOf<String, Int>()

    /**
     * Appends a new member to the end of this object, with the specified [name] and the JSON
     * representation of the specified [value].
     *
     * This method _does not prevent duplicate names_. Calling this method with a name
     * that already exists in the object will append another member with the same name. In order to
     * replace existing members, use the method [set] instead. However,
     * [add] is much faster than [set] (because it does not need to
     * search for existing members). Therefore. [add] should be preferred when constructing new
     * objects.
     *
     * @param name The name of the member to add
     *
     * @param value The value of the member to add
     *
     * @return This object; to enable method chaining (a throw-back to its Java origins).
     *         Use of the Kotlin `apply` method is preferred over chaining method calls.
     */
    fun add(name: String, value: JsonValue): JsonObject {
        indexHash[name] = names.size
        names.add(name)
        values.add(value)
        return this
    }

    fun add(name: String, value: Int) = add(name, Json.value(value))

    fun add(name: String, value: Long) = add(name, Json.value(value))

    fun add(name: String, value: Float) = add(name, Json.value(value));

    fun add(name: String, value: Double) = add(name, Json.value(value))

    fun add(name: String, value: Boolean) = add(name, Json.value(value))

    fun add(name: String, value: String?) = add(name, Json.value(value))


    /**
     * Sets the value of the member with the specified name to the JSON representation of the
     * specified [value]. If this object does not contain a member with this name, a
     * new member is added at the end of the object. If this object contains multiple members with
     * this name, only the last one is changed.
     *
     * This method should _only be used to modify existing objects_. To fill a new
     * object with members, the method [add] should be preferred which is much
     * faster (as it does not need to search for existing members).
     *
     * @param name The name of the member to replace
     *
     * @param value The value to set to the member
     *
     * @return This object; to enable method chaining (a throw-back to its Java origins).
     *         Use of the Kotlin `apply` method is preferred over chaining method calls.
     */
    fun set(name: String, value: JsonValue): JsonObject {
        val index = indexOf(name)
        if (index != - 1) {
            values.set(index, value);
        } else {
            indexHash[name] = names.size
            names.add(name)
            values.add(value)
        }
        return this;
    }

    fun set(name: String, value: Int) = set(name, Json.value(value))

    fun set(name: String, value: Long) = set(name, Json.value(value))

    fun set(name: String, value: Float) = set(name, Json.value(value))

    fun set(name: String, value: Double) = set(name, Json.value(value))

    fun set(name: String, value: Boolean) = set(name, Json.value(value))

    fun set(name: String, value: String) = set(name, Json.value(value))


    /**
     * Removes a member with the specified name from this object. If this object contains multiple
     * members with the given name, only the last one is removed. If this object does not contain a
     * member with the specified name, the object is not modified.
     *
     * @param name The name of the member to remove.
     *
     * @return This object; to enable method chaining (a throw-back to its Java origins).
     *         Use of the Kotlin `apply` method is preferred over chaining method calls.
     */
    fun remove(name: String): JsonObject {
        val index = indexHash[name]
        if (index != null) {
            indexHash.remove(name)
            names.removeAt(index)
            values.removeAt(index)
            val lastIndex = names.lastIndexOf(name)
            if (lastIndex >= 0) {
                indexHash[name] = lastIndex
            }
            // Rebuild the hash table.
            for (pair in indexHash) {
                if (pair.value > index) {
                    indexHash[pair.key] = pair.value - 1
                }
            }
        }
        return this
    }

    /**
     * Checks if a specified member is present as a child of this object. This will not test if
     * this object contains the literal `null`, [JsonValue.isNull] should be used
     * for this purpose.
     *
     * @param name The name of the member to check for.
     *
     * @return Whether the member is present
     */
    fun contains(name: String) = indexHash[name] != null

    /**
     * Copies all members of the [other] object into this object. When the [other] object contains
     * members with names that also exist in this object, the existing values in this object will be
     * replaced by the corresponding values in the specified object.
     *
     * @param other The object to merge
     *
     * @return This object; to enable method chaining (a throw-back to its Java origins).
     *         Use of the Kotlin `apply` method is preferred over chaining method calls.
     */
    fun merge(other: JsonObject): JsonObject {
        for (member in other) {
            set(member.name, member.value)
        }
        return this
    }

    /**
     * Returns the value of the member with the specified name in this object. If this object contains
     * multiple members with the given name, this method will return the last one.
     *
     * @param name The name of the member whose value is to be returned
     *
     * @return The value of the last member with the specified name, or <code>null</code> if this
     *         object does not contain a member with that name
     */
    fun get(name: String): JsonValue? {
        val index = indexOf(name)
        return if (index != - 1) values[index] else null
    }

    /**
     * Returns the <code>int</code> value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one will be picked. If this
     * member's value does not represent a JSON number or if it cannot be interpreted as Java
     * <code>int</code>, an exception is thrown.
     *
     * @param name The name of the member whose value is to be returned
     *
     * @param defaultValue The value to be returned if the requested member is missing
     *
     * @return The value of the last member with the specified name, or the given default value if
     *         this object does not contain a member with that name
     */
    fun getInt(name: String, defaultValue: Int): Int {
        val value = get(name)
        return value?.asInt() ?: defaultValue
    }

    /**
     * Returns the `Long` value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one will be picked. If this
     * member's value does not represent a JSON number or if it cannot be interpreted as
     * `Long`, an exception is thrown.
     *
     * @param name The name of the member whose value is to be returned
     *
     * @param defaultValue The value to be returned if the requested member is missing
     *
     * @return
     *          The value of the last member with the specified name, or the given default value if
     *          this object does not contain a member with that name
     */
    fun getLong(name: String, defaultValue: Long): Long {
        val value = get(name)
        return value?.asLong() ?: defaultValue
    }

    /**
     * Returns the `Float` value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one will be picked. If this
     * member's value does not represent a JSON number or if it cannot be interpreted as a
     * `Float`, an exception is thrown.
     *
     * @param name The name of the member whose value is to be returned
     *
     * @param defaultValue The value to be returned if the requested member is missing
     *
     * @return  The value of the last member with the specified name, or the given default value if
     *          this object does not contain a member with that name
     */
    fun getFloat(name: String, defaultValue: Float): Float {
        val value = get(name)
        return value?.asFloat() ?: defaultValue
    }

    /**
     * Returns the `Double` value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one will be picked. If this
     * member's value does not represent a JSON number or if it cannot be interpreted as Java
     * `Double` an exception is thrown.
     *
     * @param name
     *          the name of the member whose value is to be returned
     * @param defaultValue
     *          the value to be returned if the requested member is missing
     * @return the value of the last member with the specified name, or the given default value if
     *         this object does not contain a member with that name
     */
    fun getDouble(name: String, defaultValue: Double): Double {
        val value = get(name)
        return value?.asDouble() ?: defaultValue
    }

    /**
     * Returns the `Boolean` value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one will be picked. If this
     * member's value does not represent a JSON `true` or `false` value, an
     * exception is thrown.
     *
     * @param name
     *          the name of the member whose value is to be returned
     * @param defaultValue
     *          the value to be returned if the requested member is missing
     * @return the value of the last member with the specified name, or the given default value if
     *         this object does not contain a member with that name
     */
    fun getBoolean(name: String, defaultValue: Boolean): Boolean {
        val value = get(name)
        return value?.asBoolean() ?: defaultValue
    }

    /**
     * Returns the `String` value of the member with the specified name in this object. If
     * this object does not contain a member with this name, the given default value is returned. If
     * this object contains multiple members with the given name, the last one is picked. If this
     * member's value does not represent a JSON string, an exception is thrown.
     *
     * @param name The name of the member whose value is to be returned
     *
     * @param defaultValue The value to be returned if the requested member is missing
     *
     * @return The value of the last member with the specified name, or the given default value if
     *         this object does not contain a member with that name
     */
    fun getString(name: String, defaultValue: String): String {
        val value = get(name)
        return value?.asString() ?: defaultValue
    }

    /**
     * @return The number of members in this object
     */
    fun size() = names.size

    /**
     * @return `true` iff this object contains no members
     */
    fun isEmpty() = names.isEmpty()

    /**
     * Returns a list of the names in this object in document order. The returned list is backed by
     * this object and will reflect subsequent changes. It cannot be used to modify this object.
     * Attempts to modify the returned list will result in an exception.
     *
     * @return a list of the names in this object
     */
    fun names(): List<String> = ImmutableList(names)

    /**
     * Returns an iterator over the members of this object in document order. The returned iterator
     * cannot be used to modify this object.
     *
     * @return an iterator over the members of this object
     */
    override fun iterator(): Iterator<Member> {
        val namesIterator = names.iterator()
        val valuesIterator = values.iterator()
        return object : Iterator<Member> {

            override fun hasNext(): Boolean = namesIterator.hasNext()

            override fun next(): Member {
                val name = namesIterator.next()
                val value = valuesIterator.next()
                return Member(name, value)
            }
        }
    }

    override fun write(writer: JsonWriter) {
        writer.writeObjectOpen();
        val namesIterator = names.iterator()
        val valuesIterator = values.iterator()

        if (namesIterator.hasNext()) {
            writer.writeMemberName(namesIterator.next())
            writer.writeMemberSeparator()
            valuesIterator.next().write(writer)
            while (namesIterator.hasNext()) {
                writer.writeObjectSeparator()
                writer.writeMemberName(namesIterator.next())
                writer.writeMemberSeparator()
                valuesIterator.next().write(writer)
            }
        }
        writer.writeObjectClose()
    }

    override fun isObject() = true

    override fun asObject() = this

    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + names.hashCode()
        result = 31 * result + values.hashCode()
        return result
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null) return false
        if (other !is JsonObject) return false
        if (other::class != this::class) return false
        return names == (other.names) && values == other.values
    }

    fun indexOf(name: String): Int {
        val index = indexHash[name]
        if (index != null) {
            return index
        } else {
            return - 1
        }
    }

    /*
    TODO
    private synchronized void readObject (ObjectInputStream inputStream)
    throws IOException, ClassNotFoundException
    {
        inputStream.defaultReadObject();
        table = new HashIndexTable ();
        updateHashIndex();
    }
    */

    /**
     * Represents a member of a JSON object, a pair of a name and a value.
     */
    open class Member(
        val name: String,
        val value: JsonValue
    ) {

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + value.hashCode()
            return result
        }

        /**
         * Indicates whether a given object is `equal to` this [JsonObject]. An object is considered equal
         * if it is also a [JsonObject] and both objects contain the same members
         * _in the same order_.
         *
         * If two JsonObjects are equal, they will also produce the same JSON output.
         *
         * @param other
         *          the object to be compared with this JsonObject
         * @return `true` if the specified object is equal to this [JsonObject], `false` otherwise
         */
        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other == null) return false
            if (other !is Member) return false
            if (other::class != this::class) return false

            return name == other.name && value == other.value
        }

    }

}
